import React, { useEffect, useRef, useState, useCallback } from "react";
import { Speedometer } from "@/components/Speedometer";
import { RotateCcw, Flag } from "lucide-react";

// Build ordered tokens from a literal prompt: first = "www.", last = ".com", middle = single chars.
function buildTokens(mid) {
  return ["www.", ...mid.split(""), ".com"];
}
const PENALTY_SECONDS = 2.0;
const DQT_LIMIT = 4; // 4th mistake => DQT unless restart

export default function SprintRace() {
  const midDefault = "textracing";
  const [tokens] = useState(() => buildTokens(midDefault));
  const [idx, setIdx] = useState(0);            // next expected token
  const [mistakes, setMistakes] = useState(0);
  const [penalizedAt, setPenalizedAt] = useState({}); // one penalty per position
  const [streak, setStreak] = useState(0);      // consecutive accurate chars
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [dqt, setDqt] = useState(false);
  const [elapsed, setElapsed] = useState(0);    // ms
  const startRef = useRef(null);
  const rafRef = useRef(null);

  const tick = useCallback(() => {
    if (startRef.current != null) setElapsed(performance.now() - startRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, []);
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const charsDone = tokens.slice(0, idx).reduce((n, t) => n + t.length, 0);
  const secs = elapsed / 1000;
  const cps = secs > 0 ? charsDone / secs : 0;
  const penaltyTotal = mistakes * PENALTY_SECONDS;
  const official = secs + penaltyTotal;

  // unique keyboard buttons: anchors + unique middle chars
  const midChars = Array.from(new Set(midDefault.split("")));

  const start = () => { startRef.current = performance.now(); setRunning(true); rafRef.current = requestAnimationFrame(tick); };

  const press = (tok) => {
    if (finished || dqt) return;
    // Race can only begin by pressing the "www." anchor first.
    if (!running) { if (tok !== "www.") return; start(); }
    const expected = tokens[idx];
    if (tok === expected) {
      const next = idx + 1;
      setIdx(next); setStreak((s) => s + tok.length);
      if (next >= tokens.length) { // pressed ".com" as last -> stop clock instantly
        cancelAnimationFrame(rafRef.current);
        setElapsed(performance.now() - startRef.current);
        setFinished(true); setRunning(false);
      }
    } else {
      // wrong token: cursor holds; penalty only once per position
      setStreak(0);
      if (!penalizedAt[idx]) {
        setPenalizedAt((p) => ({ ...p, [idx]: true }));
        const m = mistakes + 1; setMistakes(m);
        if (m >= DQT_LIMIT) { cancelAnimationFrame(rafRef.current); setDqt(true); setRunning(false); }
      }
    }
  };

  const restart = () => { // pit stop: clears mistakes, but the clock keeps running (one clock per contest)
    setIdx(0); setMistakes(0); setPenalizedAt({}); setStreak(0); setDqt(false); setFinished(false);
    if (startRef.current == null) { start(); } else { setRunning(true); rafRef.current = requestAnimationFrame(tick); }
  };

  const fmt = (s) => { const v = Math.max(0, s); return `${v.toFixed(3)}`; };
  const tireOn = (n) => mistakes >= n;

  return (
    <div className="max-w-md mx-auto" data-testid="sprint-race-screen">
      {/* In-race ad */}
      <div className="bg-race-surface border border-race-line rounded-sm h-16 flex items-center justify-center text-race-sub text-xs mb-2" data-testid="sprint-inrace-ad">In-Race Advertisement</div>

      <div className="bg-white text-black rounded-sm p-3">
        {/* sub-header */}
        <div className="flex justify-between items-center mb-3">
          <div className="font-heading text-lg" data-testid="sprint-name">SPRINT DEMO</div>
          <div className="font-mono text-lg tabular" data-testid="sprint-clock">{fmt(secs)}s</div>
        </div>

        {/* tires + center column */}
        <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-stretch">
          <div className="flex flex-col justify-between">
            <Tire on={tireOn(1)} testid="sprint-tire-1" />
            <Tire on={tireOn(3)} testid="sprint-tire-3" />
          </div>

          <div className="flex flex-col items-center gap-3">
            {/* Character Track Prompt */}
            <div className="flex flex-wrap justify-center gap-1 min-h-[48px]" data-testid="sprint-prompt">
              {tokens.map((t, i) => (
                <span key={i}
                  className={`font-mono text-sm px-1 rounded ${i < idx ? "text-green-600" : i === idx ? (penalizedAt[i] ? "bg-red-500 text-white" : "bg-black text-white") : "text-gray-400"} ${t === ".com" ? "whitespace-nowrap" : ""}`}>
                  {t}
                </span>
              ))}
            </div>
            {/* Speedometer (live) */}
            <Speedometer cps={cps} accurateStreak={streak} unit="mph" distanceMPerChar={10} />
            <div className="text-[10px] font-mono text-gray-600">+{penaltyTotal.toFixed(1)}s PEN · OFFICIAL {fmt(official)}s</div>
          </div>

          <div className="flex flex-col justify-between">
            <Tire on={tireOn(2)} testid="sprint-tire-2" />
            <Tire on={tireOn(4)} testid="sprint-tire-4" />
          </div>
        </div>

        {/* Keyboard */}
        <div className="flex flex-wrap gap-1.5 justify-center mt-3" data-testid="sprint-keyboard">
          <Key label="www." onClick={() => press("www.")} testid="sprint-key-www" accent />
          {midChars.map((c) => <Key key={c} label={c} onClick={() => press(c)} testid={`sprint-key-${c}`} />)}
          <Key label=".com" onClick={() => press(".com")} testid="sprint-key-com" accent />
        </div>

        {/* status */}
        {dqt && (
          <div className="mt-3 text-center" data-testid="sprint-dqt">
            <div className="text-red-600 font-heading text-xl">DQT - 4 mistakes</div>
            <button onClick={restart} data-testid="sprint-restart" className="mt-2 inline-flex items-center gap-1.5 bg-black text-white rounded-sm px-4 py-2 text-sm"><RotateCcw size={14} /> Pit-stop restart (clock keeps running)</button>
          </div>
        )}
        {finished && (
          <div className="mt-3 text-center text-green-700 font-heading text-xl flex items-center justify-center gap-2" data-testid="sprint-finished"><Flag size={18} /> Finish - Official {fmt(official)}s</div>
        )}
        {!running && !finished && !dqt && (
          <div className="mt-3 text-center text-gray-500 text-xs" data-testid="sprint-hint">Tap <b>www.</b> to start the clock.</div>
        )}
      </div>

      {/* real-time position placeholder (logic TBD per RACE LOGIC) */}
      <div className="bg-race-surface border border-race-line rounded-sm p-3 mt-2 text-race-sub text-xs" data-testid="sprint-positions">Real-time Racing Position - logic coming soon.</div>
    </div>
  );
}

const Tire = ({ on, testid }) => (
  <div data-testid={testid} className={`w-6 h-14 rounded-sm border-2 ${on ? "bg-red-600 border-red-400" : "bg-black border-green-400"}`} />
);
const Key = ({ label, onClick, testid, accent }) => (
  <button onClick={onClick} data-testid={testid}
    className={`font-mono text-sm rounded px-3 py-2 border ${accent ? "bg-black text-white border-black" : "bg-gray-100 text-black border-gray-300"} active:scale-95 transition-transform`}>
    {label}
  </button>
);
